import './app.css'
import './bootstrap'
import 'easymde/dist/easymde.min.css'
import '@fortawesome/fontawesome-free/css/all.min.css'
import 'flag-icons/css/flag-icons.min.css'
import * as Sentry from '@sentry/vue'
import { createApp } from 'vue'
import App from './components/App.vue'
import router from './router/index.ts'
import i18n from './i18n/index.ts'
import client from './api/client.ts'
import { useAuth } from './composables/useAuth.ts'
import { scrubEvent } from './lib/sentry.ts'
import { trackPlatformPageViews } from './router/platformPageViews.ts'

// Redirect to the participant login page on 401 when the session expires mid-page.
// Only activates for participant-auth-required routes to avoid interfering with the
// organizer zone which handles authentication through its own router guard.
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const currentRoute = router.currentRoute.value
      if (currentRoute.meta.requiresParticipantAuth) {
        const { user } = useAuth()
        user.value = null
        router.push({ name: 'participant.login' })
      }
    }
    return Promise.reject(error)
  },
)

const app = createApp(App)

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN as string | undefined,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined,
  release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
  // No-op when DSN is absent (local dev / CI without secrets).
  enabled: Boolean(import.meta.env.VITE_SENTRY_DSN),
  // Hard requirement per spec §4: never send cookies, IP, headers, or user fields.
  sendDefaultPii: false,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
  ],
  // Free-tier safe: tracing off by default. Raise temporarily for latency work.
  tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0),
  // Session Replay intentionally NOT included — quota + PII risk.
  beforeSend: scrubEvent,
})

// Registered before app.use(router) so the initial navigation is captured.
trackPlatformPageViews(router)

app.use(router).use(i18n).mount('#app')
